import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import {
    BridgeConnection,
    CreateConnectionRequest,
    TestConnectionResponse,
    UpdateConnectionRequest
} from "../../../models/bridge/bridge-connection.model";
import { BridgeBaseService } from "./bridge-base.service";

/**
 * Service for 4Bridge Connections API
 */
@Injectable({
    providedIn: 'root'
})
export class BridgeConnectionService extends BridgeBaseService<BridgeConnection> {

    constructor(httpClient: HttpClient) {
        super(httpClient, 'connections');
    }

    /**
     * Create a new connection
     */
    public createConnection(request: CreateConnectionRequest): Observable<BridgeConnection> {
        return this.create(request as any);
    }

    /**
     * Update an existing connection
     */
    public updateConnection(id: string, request: UpdateConnectionRequest): Observable<BridgeConnection> {
        return this.update(id, request as any);
    }

    /**
     * Test a connection with credentials from form (not from database)
     * @param id Connection ID (for existing connections)
     * @param credentials The credentials to test
     */
    public testConnection(
        id: string,
        credentials?: { connectorType?: string; config?: Record<string, any>; secrets?: Record<string, string> }
    ): Observable<TestConnectionResponse> {
        const body = credentials || {};
        return this.httpClient
            .post<TestConnectionResponse>(`${this.baseUrl}/${this.endpoint}/${id}/test`, body)
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Test credentials without an existing connection (for new connections)
     * @param credentials The credentials to test
     */
    public testCredentials(
        credentials: { connectorType: string; config?: Record<string, any>; secrets: Record<string, string> }
    ): Observable<TestConnectionResponse> {
        return this.httpClient
            .post<TestConnectionResponse>(`${this.baseUrl}/${this.endpoint}/test`, credentials)
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Get connections by connector type
     */
    public getByConnectorType(connectorType: string): Observable<BridgeConnection[]> {
        return this.httpClient
            .get<BridgeConnection[]>(`${this.baseUrl}/${this.endpoint}?connectorType=${connectorType}`)
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Get available objects for a connection
     * @param connectionId The connection ID
     */
    public getObjects(connectionId: string): Observable<ConnectionObject[]> {
        return this.httpClient
            .get<ConnectionObject[]>(`${this.baseUrl}/${this.endpoint}/${connectionId}/objects`)
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Get fields for a specific object in a connection
     * @param connectionId The connection ID
     * @param objectName The object name (e.g., 'Contacts', 'Permissions')
     */
    public getObjectFields(connectionId: string, objectName: string): Observable<ConnectionField[]> {
        return this.httpClient
            .get<ConnectionField[]>(`${this.baseUrl}/${this.endpoint}/${connectionId}/objects/${objectName}/fields`)
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * D-13: Refresh metadata cache for a specific object in a connection.
     * Clears the server-side cache (24h TTL) and re-fetches fresh field metadata from the live connector.
     * @param connectionId The connection ID
     * @param objectName The object name to refresh
     */
    public refreshMetadata(connectionId: string, objectName: string): Observable<ConnectionField[]> {
        return this.httpClient
            .post<ConnectionField[]>(
                `${this.baseUrl}/${this.endpoint}/${connectionId}/metadata/refresh?objectName=${encodeURIComponent(objectName)}`,
                {}
            )
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }
}

/**
 * Object available in a connection
 */
export interface ConnectionObject {
    name: string;
    label: string;
    description?: string;
    supportsCreate: boolean;
    supportsRead: boolean;
    supportsUpdate: boolean;
    supportsDelete: boolean;
}

/**
 * Field in a connection object
 */
export interface ConnectionField {
    name: string;
    label: string;
    type: string;
    isRequired: boolean;
    isReadOnly: boolean;
    description?: string;
}
